import { Link } from "react-router-dom";
import { useState } from "react";
import {
  BookOpen, Users, Building2, Shield, BarChart3, ChevronRight,
  Play, CheckCircle, ArrowRight, Zap, Globe, Award, Star, Lock,
  Layers, LineChart, Settings, MonitorSmartphone, Headphones,
  ArrowUpRight, ChevronDown, MessageSquare, Target, Cpu
} from "lucide-react";
import { Button } from "../components/ui/button";
import Logo from "../components/shared/Logo";

const HERO_IMG = "https://images.unsplash.com/photo-1758691736067-b309ee3ef7b9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxlbnRlcnByaXNlJTIwdGVhbSUyMHRyYWluaW5nJTIwY29ycG9yYXRlJTIwbGVhcm5pbmd8ZW58MHx8fHwxNzc0NDM4OTIwfDA&ixlib=rb-4.1.0&q=85";
const TEAM_IMG = "https://images.pexels.com/photos/7792803/pexels-photo-7792803.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const ANALYTICS_IMG = "https://images.pexels.com/photos/577195/pexels-photo-577195.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const stats = [
  { value: "10K+", label: "Active Learners", sub: "across enterprises" },
  { value: "500+", label: "Courses Delivered", sub: "SCORM & custom" },
  { value: "98%", label: "Completion Rate", sub: "industry leading" },
  { value: "50+", label: "Enterprise Clients", sub: "Fortune 500 included" },
];

const platformFeatures = [
  { icon: BookOpen, title: "SCORM 1.2 & 2004", desc: "Upload and deliver Articulate, Storyline, and Captivate courses with full xAPI tracking and automatic completion certificates." },
  { icon: Users, title: "Instructor-Led Training", desc: "Schedule live sessions, manage waitlists, send calendar invites, and track attendance with automated reminders." },
  { icon: Building2, title: "Multi-Tenant Architecture", desc: "Deploy isolated, white-labeled portals per client with custom domains, branding, and RBAC-driven access controls." },
  { icon: Shield, title: "Compliance & Safety", desc: "Track mandatory certifications, set recertification schedules, send automated alerts, and generate audit-ready reports." },
  { icon: BarChart3, title: "Advanced Analytics", desc: "Real-time dashboards with learner heatmaps, skill gaps, completion funnels, and executive-level KPI reporting." },
  { icon: Globe, title: "Embed Anywhere (LTI 1.3)", desc: "Integrate courses into Moodle, Canvas, Blackboard, and 10+ LMS platforms via iFrame or LTI 1.3 with guided setup." },
  { icon: Lock, title: "SSO & Security", desc: "SAML 2.0 SSO, role-based access, tenant isolation middleware, data encryption at rest and in transit." },
  { icon: Cpu, title: "AI-Powered Insights", desc: "AI risk scoring, learning path recommendations, compliance gap detection, and automated reporting." },
  { icon: MonitorSmartphone, title: "PWA & Mobile Ready", desc: "Progressive Web App with push notifications, offline access, and responsive design for any device." },
];

const industries = [
  { title: "Manufacturing", desc: "Safety protocols, equipment certification, standard operating procedures", icon: Settings },
  { title: "Healthcare", desc: "HIPAA compliance, clinical training, continuing medical education", icon: Shield },
  { title: "Energy & Utilities", desc: "Regulatory compliance, field safety, operational procedures", icon: Zap },
  { title: "Financial Services", desc: "AML/KYC training, regulatory updates, risk management", icon: LineChart },
];

const testimonials = [
  { quote: "Odesie transformed how we deliver safety training across 40+ manufacturing sites. Compliance rates went from 72% to 98% in six months.", author: "Sarah Mitchell", role: "VP of Learning & Development", company: "Global Manufacturing Corp", rating: 5 },
  { quote: "The multi-tenant architecture lets us serve each of our enterprise clients with their own branded portal. It's a game-changer for our training business.", author: "James Rodriguez", role: "Chief Operating Officer", company: "TTS Learn Hub", rating: 5 },
  { quote: "We migrated from Absorb LMS in under a week. The ETL tool imported 5,000 users and 200 courses without a single data issue.", author: "Dr. Priya Sharma", role: "Director of Training", company: "MedTech Solutions", rating: 5 },
];

const pricingTiers = [
  { name: "Starter", price: "Contact Us", period: "", desc: "For small teams getting started", features: ["Up to 100 learners", "5 admin users", "SCORM hosting", "Basic analytics", "Email support"], highlighted: false },
  { name: "Professional", price: "Contact Us", period: "", desc: "For growing organizations", features: ["Up to 1,000 learners", "25 admin users", "ILT + SCORM", "Advanced analytics", "SSO integration", "Embed engine", "Priority support"], highlighted: true },
  { name: "Enterprise", price: "Custom", period: "", desc: "For large-scale deployments", features: ["Unlimited learners", "Unlimited admins", "Multi-tenant", "AI insights", "LTI 1.3", "Dedicated CSM", "SLA guarantee", "Custom integrations"], highlighted: false },
];

const faqs = [
  { q: "How long does implementation take?", a: "Most enterprises are live within 2-4 weeks. Our onboarding wizard guides you through tenant setup, branding, course uploads, and user provisioning. Migration from Absorb, Docebo, or other LMS platforms is supported via our ETL tool." },
  { q: "Does Odesie support SCORM and xAPI?", a: "Yes. We support SCORM 1.2, SCORM 2004, and xAPI (Tin Can) standards. Upload your Articulate Storyline, Adobe Captivate, or iSpring packages and they'll work out of the box with full progress tracking." },
  { q: "Can I white-label the platform for my clients?", a: "Absolutely. Each tenant gets their own branded portal with custom logos, colors, domains, and email templates. Your clients never see the Odesie brand — it looks and feels like your own product." },
  { q: "What security certifications does Odesie have?", a: "Odesie implements enterprise-grade security: SAML 2.0 SSO, tenant isolation middleware, role-based access control, encrypted data at rest, and audit logging. We support SOC 2 compliance requirements." },
  { q: "Can I embed courses into our existing LMS?", a: "Yes. Our Embed Engine supports LTI 1.3 and iFrame integration with 10+ LMS platforms including Moodle, Canvas, Blackboard, Brightspace, Docebo, and more. Each embed comes with platform-specific setup instructions." },
];

const FaqItem = ({ item }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border/60">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-5 text-left group">
        <span className="font-semibold text-foreground group-hover:text-[#1B4F8A] transition-colors pr-4" style={{ fontFamily: "'Sora', sans-serif" }}>{item.q}</span>
        <ChevronDown className={`w-5 h-5 text-muted-foreground flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p className="pb-5 text-muted-foreground leading-relaxed text-[15px] -mt-1">{item.a}</p>}
    </div>
  );
};

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="flex items-center justify-between h-[72px]">
            <div className="flex items-center gap-10">
              <Logo size="md" variant="light" />
              <div className="hidden lg:flex items-center gap-1">
                {[
                  { label: "Platform", href: "#platform" },
                  { label: "Solutions", href: "#solutions" },
                  { label: "Pricing", href: "#pricing" },
                  { label: "Resources", href: "#faq" },
                ].map(link => (
                  <a key={link.label} href={link.href} className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-50 transition-all">{link.label}</a>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/auth/login">
                <Button variant="ghost" size="sm" className="text-sm font-medium text-gray-600 hover:text-gray-900" data-testid="nav-login-btn">Sign In</Button>
              </Link>
              <Link to="/auth/signup">
                <Button size="sm" className="bg-[#1B4F8A] hover:bg-[#153F6E] text-white text-sm font-semibold px-5 h-9 rounded-lg shadow-sm" data-testid="nav-signup-btn">Get Started Free</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-[120px] pb-0 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50/60 via-white to-white pointer-events-none" />
        <div className="max-w-[1280px] mx-auto px-6 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="py-12 lg:py-20">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-[#1B4F8A] text-xs font-bold tracking-wide uppercase mb-6">
                <Zap className="w-3.5 h-3.5" /> Enterprise LMS Platform
              </div>
              <h1 className="text-[2.75rem] sm:text-[3.25rem] lg:text-[3.75rem] font-extrabold text-gray-900 leading-[1.1] mb-6 tracking-tight" style={{ fontFamily: "'Sora', sans-serif" }}>
                Train your workforce.<br />
                <span className="bg-gradient-to-r from-[#1B4F8A] to-[#0E8A55] bg-clip-text text-transparent">Scale with confidence.</span>
              </h1>
              <p className="text-lg text-gray-500 mb-8 max-w-[520px] leading-relaxed">
                The enterprise learning platform built for compliance, safety, and operational training. Deploy branded portals, track certifications, and deliver SCORM courses — all from one place.
              </p>
              <div className="flex flex-wrap gap-4 mb-10">
                <Link to="/auth/signup">
                  <Button className="bg-[#1B4F8A] hover:bg-[#153F6E] text-white text-base font-semibold px-7 h-12 rounded-xl shadow-md hover:shadow-lg transition-all" data-testid="hero-cta-btn">
                    Start Free Trial <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Button variant="outline" className="text-base px-7 h-12 rounded-xl border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50" data-testid="hero-demo-btn">
                  <Play className="w-5 h-5 mr-2 text-[#1B4F8A]" /> Watch Demo
                </Button>
              </div>
              <div className="flex items-center gap-6 text-sm text-gray-400">
                <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-[#0E8A55]" /> No credit card</span>
                <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-[#0E8A55]" /> 14-day free trial</span>
                <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-[#0E8A55]" /> Cancel anytime</span>
              </div>
            </div>
            <div className="hidden lg:block relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-gray-200/60">
                <img src={HERO_IMG} alt="Enterprise training session" className="w-full h-[440px] object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/30 via-transparent to-transparent" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg border border-gray-100 p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center"><CheckCircle className="w-5 h-5 text-green-600" /></div>
                <div><p className="font-bold text-sm text-gray-900">98% Completion</p><p className="text-xs text-gray-400">Industry leading rate</p></div>
              </div>
              <div className="absolute -top-4 -right-4 bg-white rounded-xl shadow-lg border border-gray-100 p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center"><Users className="w-5 h-5 text-[#1B4F8A]" /></div>
                <div><p className="font-bold text-sm text-gray-900">10K+ Learners</p><p className="text-xs text-gray-400">Across 50+ enterprises</p></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Logos / Social Proof Bar */}
      <section className="py-12 border-y border-gray-100 bg-gray-50/50">
        <div className="max-w-[1280px] mx-auto px-6">
          <p className="text-center text-xs font-bold text-gray-400 uppercase tracking-widest mb-8">Trusted by enterprises across industries</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((s, i) => (
              <div key={i} className="text-center">
                <p className="text-3xl sm:text-4xl font-extrabold text-gray-900" style={{ fontFamily: "'Sora', sans-serif" }}>{s.value}</p>
                <p className="text-sm font-semibold text-gray-600 mt-1">{s.label}</p>
                <p className="text-xs text-gray-400 mt-0.5">{s.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Features */}
      <section id="platform" className="py-24 bg-white">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-[#1B4F8A] text-xs font-bold tracking-wide uppercase mb-4">
              <Layers className="w-3.5 h-3.5" /> Platform
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4 tracking-tight" style={{ fontFamily: "'Sora', sans-serif" }}>
              Everything you need for enterprise training
            </h2>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">
              A complete learning management platform built for compliance, safety, and operational excellence at scale.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {platformFeatures.map((f, i) => (
              <div key={i} className="group p-6 rounded-2xl border border-gray-100 bg-white hover:border-blue-100 hover:shadow-lg transition-all duration-300" data-testid={`feature-card-${i}`}>
                <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center mb-4 group-hover:bg-[#1B4F8A] group-hover:text-white transition-colors">
                  <f.icon className="w-5 h-5 text-[#1B4F8A] group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2" style={{ fontFamily: "'Sora', sans-serif" }}>{f.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Visual Showcase */}
      <section className="py-24 bg-gray-50/70">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-50 border border-green-100 text-green-700 text-xs font-bold tracking-wide uppercase mb-4">
                <Target className="w-3.5 h-3.5" /> Results
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-6 tracking-tight" style={{ fontFamily: "'Sora', sans-serif" }}>
                Built for measurable impact
              </h2>
              <p className="text-lg text-gray-500 mb-8 leading-relaxed">
                Organizations using Odesie report significant improvements in compliance rates, training completion, and time-to-competency across their workforce.
              </p>
              <div className="space-y-5">
                {[
                  { metric: "40%", label: "Reduction in compliance incidents after 6 months" },
                  { metric: "3x", label: "Faster onboarding for new employees" },
                  { metric: "60%", label: "Lower training delivery costs vs. classroom" },
                  { metric: "98%", label: "Average course completion rate" },
                ].map((r, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <span className="text-2xl font-extrabold text-[#1B4F8A] w-16 flex-shrink-0" style={{ fontFamily: "'Sora', sans-serif" }}>{r.metric}</span>
                    <span className="text-gray-600 pt-1">{r.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-xl border border-gray-200/60">
                <img src={ANALYTICS_IMG} alt="Analytics dashboard" className="w-full h-[400px] object-cover" loading="lazy" />
              </div>
              <div className="absolute -bottom-5 -left-5 bg-white rounded-xl shadow-lg border border-gray-100 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center"><LineChart className="w-5 h-5 text-amber-600" /></div>
                  <div><p className="font-bold text-sm text-gray-900">Real-Time Reports</p><p className="text-xs text-gray-400">Executive dashboards</p></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Solutions */}
      <section id="solutions" className="py-24 bg-white">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-50 border border-purple-100 text-purple-700 text-xs font-bold tracking-wide uppercase mb-4">
              <Building2 className="w-3.5 h-3.5" /> Solutions
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4 tracking-tight" style={{ fontFamily: "'Sora', sans-serif" }}>
              Purpose-built for regulated industries
            </h2>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">
              Odesie serves enterprises where training isn't optional — it's a business requirement.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {industries.map((ind, i) => (
              <div key={i} className="p-6 rounded-2xl border border-gray-100 bg-white hover:shadow-lg hover:border-purple-100 transition-all duration-300 group" data-testid={`industry-card-${i}`}>
                <div className="w-11 h-11 rounded-xl bg-purple-50 flex items-center justify-center mb-4 group-hover:bg-purple-600 transition-colors">
                  <ind.icon className="w-5 h-5 text-purple-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2" style={{ fontFamily: "'Sora', sans-serif" }}>{ind.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gray-50/70">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-100 text-amber-700 text-xs font-bold tracking-wide uppercase mb-4">
              <MessageSquare className="w-3.5 h-3.5" /> Testimonials
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4 tracking-tight" style={{ fontFamily: "'Sora', sans-serif" }}>
              Trusted by training leaders
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 p-8 shadow-sm hover:shadow-md transition-shadow" data-testid={`testimonial-${i}`}>
                <div className="flex gap-0.5 mb-4">
                  {Array.from({ length: t.rating }).map((_, j) => <Star key={j} className="w-4 h-4 text-amber-400 fill-amber-400" />)}
                </div>
                <p className="text-gray-600 leading-relaxed mb-6 text-[15px]">"{t.quote}"</p>
                <div className="border-t border-gray-100 pt-4">
                  <p className="font-bold text-sm text-gray-900">{t.author}</p>
                  <p className="text-xs text-gray-400">{t.role}, {t.company}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-white">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4 tracking-tight" style={{ fontFamily: "'Sora', sans-serif" }}>
              Go live in under 2 weeks
            </h2>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">
              Our guided onboarding gets your enterprise training program running fast.
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: "01", title: "Configure", desc: "Set up your tenant, branding, roles, and SSO in our guided wizard." },
              { step: "02", title: "Import", desc: "Upload courses, migrate users from Absorb or Docebo, and set up learning paths." },
              { step: "03", title: "Deploy", desc: "Embed courses via LTI 1.3, invite learners, and assign compliance tracks." },
              { step: "04", title: "Measure", desc: "Track completion rates, compliance gaps, and learner engagement in real time." },
            ].map((s, i) => (
              <div key={i} className="text-center relative">
                <div className="w-14 h-14 rounded-2xl bg-[#1B4F8A] text-white flex items-center justify-center mx-auto mb-5 text-lg font-extrabold" style={{ fontFamily: "'Sora', sans-serif" }}>{s.step}</div>
                {i < 3 && <div className="hidden md:block absolute top-7 left-[calc(50%+28px)] w-[calc(100%-56px)] h-[2px] bg-gray-200" />}
                <h3 className="font-bold text-gray-900 mb-2" style={{ fontFamily: "'Sora', sans-serif" }}>{s.title}</h3>
                <p className="text-sm text-gray-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 bg-gray-50/70">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-[#1B4F8A] text-xs font-bold tracking-wide uppercase mb-4">
              <Award className="w-3.5 h-3.5" /> Pricing
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4 tracking-tight" style={{ fontFamily: "'Sora', sans-serif" }}>
              Plans that scale with you
            </h2>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">
              Start free, upgrade as your training program grows. Volume discounts available.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {pricingTiers.map((tier, i) => (
              <div key={i} className={`rounded-2xl p-8 flex flex-col ${tier.highlighted ? 'bg-[#1B4F8A] text-white ring-2 ring-[#1B4F8A] shadow-xl relative' : 'bg-white border border-gray-100 shadow-sm'}`} data-testid={`pricing-${tier.name.toLowerCase()}`}>
                {tier.highlighted && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#0E8A55] text-white text-xs font-bold rounded-full uppercase tracking-wide">Most Popular</div>}
                <h3 className="text-lg font-bold mb-1" style={{ fontFamily: "'Sora', sans-serif" }}>{tier.name}</h3>
                <p className={`text-sm mb-5 ${tier.highlighted ? 'text-white/70' : 'text-gray-400'}`}>{tier.desc}</p>
                <p className="text-3xl font-extrabold mb-6" style={{ fontFamily: "'Sora', sans-serif" }}>{tier.price}</p>
                <ul className="space-y-3 mb-8 flex-grow">
                  {tier.features.map((f, j) => (
                    <li key={j} className="flex items-center gap-2.5 text-sm">
                      <CheckCircle className={`w-4 h-4 flex-shrink-0 ${tier.highlighted ? 'text-green-300' : 'text-[#0E8A55]'}`} />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link to="/auth/signup">
                  <Button className={`w-full h-11 font-semibold rounded-xl ${tier.highlighted ? 'bg-white text-[#1B4F8A] hover:bg-gray-100' : 'bg-[#1B4F8A] text-white hover:bg-[#153F6E]'}`}>
                    Get Started <ArrowUpRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4 tracking-tight" style={{ fontFamily: "'Sora', sans-serif" }}>
              Frequently asked questions
            </h2>
          </div>
          <div>
            {faqs.map((faq, i) => <FaqItem key={i} item={faq} />)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-[#1B4F8A] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 50%, white 0%, transparent 50%), radial-gradient(circle at 80% 50%, white 0%, transparent 50%)" }} />
        <div className="max-w-3xl mx-auto px-6 text-center relative">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-6 tracking-tight" style={{ fontFamily: "'Sora', sans-serif" }}>
            Ready to transform your enterprise training?
          </h2>
          <p className="text-lg text-white/70 mb-8 max-w-xl mx-auto">
            Join 50+ organizations that trust Odesie to deliver compliant, scalable training programs across their workforce.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/auth/signup">
              <Button className="bg-white text-[#1B4F8A] hover:bg-gray-100 text-base font-semibold px-8 h-12 rounded-xl shadow-lg" data-testid="cta-get-started-btn">
                Start Free Trial <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Button variant="outline" className="text-base px-8 h-12 rounded-xl bg-transparent text-white border-white/30 hover:bg-white/10 font-semibold" data-testid="cta-contact-btn">
              <Headphones className="w-5 h-5 mr-2" /> Talk to Sales
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-16 px-6">
        <div className="max-w-[1280px] mx-auto">
          <div className="grid md:grid-cols-4 gap-10 mb-12">
            <div>
              <Logo size="sm" variant="dark" />
              <p className="text-sm mt-4 leading-relaxed">Enterprise learning management for compliance, safety, and operational training at scale.</p>
            </div>
            <div>
              <h4 className="font-bold text-white text-sm mb-4" style={{ fontFamily: "'Sora', sans-serif" }}>Platform</h4>
              <ul className="space-y-2.5 text-sm">
                {["Course Management", "ILT Sessions", "Compliance Tracking", "Analytics", "Embed Engine", "LTI 1.3"].map(l => <li key={l}><a href="#platform" className="hover:text-white transition-colors">{l}</a></li>)}
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-white text-sm mb-4" style={{ fontFamily: "'Sora', sans-serif" }}>Company</h4>
              <ul className="space-y-2.5 text-sm">
                {["About Us", "Careers", "Blog", "Contact", "Partner Program"].map(l => <li key={l}><a href="#" className="hover:text-white transition-colors">{l}</a></li>)}
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-white text-sm mb-4" style={{ fontFamily: "'Sora', sans-serif" }}>Legal</h4>
              <ul className="space-y-2.5 text-sm">
                {["Privacy Policy", "Terms of Service", "Security", "GDPR", "SOC 2"].map(l => <li key={l}><a href="#" className="hover:text-white transition-colors">{l}</a></li>)}
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm">&copy; 2026 Technology Transfer Services, LLC. All rights reserved.</p>
            <p className="text-xs text-gray-500">Powered by Flynaut</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
